'use strict';

const _ = require('lodash');

const _ROLE_ORG_ADMIN = 10;
const _ROLE_ORG_MODERATOR = 9;

const IdentityService = function ($document, jwtHelper, StorageUtil) {

  // cache data to skip querying local storage
  let cache = {};

  /////////////////////////////////////////////////////////////////////////////////////////////////
  // Getters
  /////////////////////////////////////////////////////////////////////////////////////////////////

  const get = (key) => {

    let data;

    if (cache[key]) {
      data = cache[key];
    }
    else {
      data = StorageUtil.get(key);
      cache[key] = data;
    }

    return data;
  };

  const getAll = () => {

    return cache;
  };

  const getApiUri = () => {

    return get('coreApiUri');
  };

  const getAuthApiUri = () => {

    return get('authApiUri');
  };

  const getCurrentOrganization = () => {

    return get('currentOrganization');
  };

  const getFirebaseUri = (dbname) => {

    const data = get('firebase');
    dbname = dbname || 'apollo';

    if (!data) {
      return null;
    }
    else {
      return (data[dbname] && data[dbname].uri) || 'baduri';
    }
  };

  const getFirebaseToken = (dbname) => {

    const data = get('firebase');
    dbname = dbname || 'apollo';

    if (!data) {
      return null;
    }
    else {
      return (data[dbname] && data[dbname].token) || 'badtoken';
    }
  };

  const getPaymentsApiUri = () => {

    return get('paymentsApiUri');
  };

  const getResourceProxyUri = () => {

    return get('resourceProxyUri');
  };

  // --------- INGESTOR ADDS ---------
  const getIngestApiUri = () => {

    return get('ingestApiUri');
  };

  const getGeopingApiUri = () => {

    return get('geopingApiUri');
  };

  const getSkyApiUri = (version) => {
    version = version || 'v2'
    return joinPath(get('skyApiUri'), version)
  }

  // --------- END INGESTOR ADDS ---------

  const getToken = () => {

    const token = get('token');
    return jwtHelper.decodeToken(token);
  };

  const getUser = () => {

    return get('user');
  };

  const getEnv = () => {

    return get('env') || 'development';
  };

  const getCurrentGroup = () => {

    return get('currentGroup');
  };

  const getUserLevel = () => {

    return get('currentGroupAccess');
  };

  const isOrganizationAdmin = () => {

    const organization = get('currentOrganization');
    return organization._pivot_roleId === _ROLE_ORG_ADMIN || organization._pivot_roleId === _ROLE_ORG_MODERATOR;
  };

  const isOrganizationBillingAdmin = () => {

    const organization = get('currentOrganization');
    return organization._pivot_roleId === _ROLE_ORG_ADMIN;
  };

  const isAnonUser = () => {

    const user = get('user');

    if (!user || (user && user.group === 'anon')) {
      return true;
    }
    else {
      return false;
    }
  };

  const isBetaUser = () => {

    const user = get('user');

    if (user && user.meta && _.find(user.meta, { key: 'measurementsEnabled'})) {
      return true;
    }

    return false;
  };

  /**
   * @returns {boolean} True if user should have access to ingestor, false if not. Defaults to true
   */
  const isIngestBetaUser = () => {

    const user = get('user');

    if (user && user.meta && _.find(user.meta, { key: 'ingestEnabled'})) {
      const ingestState = _.find(user.meta, { key: 'ingestEnabled'})
      return ingestState.value === 1 || ingestState.value === "1" || ingestState.value === "true";
    }

    // Default to true to indicate GA
    // User can be switched back to the old uploader by setting ingestEnabled to `false` in the DB
    return true;
  };

  const getBetaFeatures = () => {

    const user = get('user');

    const features = {};
    if (user && user.meta) {

      user.meta.forEach((meta) => {

        const match = meta.key.match(/^beta_(.*)/);
        if (match && meta.value) {
          features[match[1]] = true;
        }
      });

      // Special cases... should move to the db
      if (user.email && user.email.match(/@skycatch.com$/)) {
        features.mapboxWebgl = true;
      }
    }

    return features;
  };

  const isAdminUser = () => {

    const user = get('user');

    if (user) {
      return user.isAdmin;
    }
    else {
      return false;
    }
  };


  /////////////////////////////////////////////////////////////////////////////////////////////////
  // Setters
  /////////////////////////////////////////////////////////////////////////////////////////////////

  const init = () => {

    extractInitData();
  };

  const clearAuth = () => {

    remove('user');
    remove('firebase');
  };

  const set = (key, val) => {

    cache[key] = val;
    StorageUtil.set(key, val);
  };

  const updateUserMeta = (meta = []) => {

    const user = get('user');

    user.meta = meta;
    set('user', user);
  };

  const remove = (key) => {

    delete cache[key];
    StorageUtil.remove(key);
  };

  /**
   * Currently only used for testing
   * Will probably be used when the user logs out
   *
   * Resets the cache/localStorage to it's pristine state
   */
  const $$clearCache = () => {

    _.forEach(cache, (val, key) => {
      StorageUtil.remove(key);
    });

    cache = {};
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////
  // Helpers
  /////////////////////////////////////////////////////////////////////////////////////////////////

  const joinPath = (base, path) => {

    if (base.indexOf('http') === -1) {
      if (window.location.protocol.indexOf('https') !== -1) {
        base = 'https://' + base;
      }
      else {
        base = 'http://' + base;
      }
    }
    return base.replace(/\/$/, '') + '/' + path.replace(/^\//, '');
  };

  const extractInitData = () => {

    const initDataEl = $document[0].querySelector('#init-data');
    const initData = initDataEl && JSON.parse(initDataEl.getAttribute('value'));

    if (initData) {
      set('env', initData.appInfo.env);
      set('coreApiUri', joinPath(initData.coreApi.url, initData.coreApi.basePath));
      set('authApiUri', joinPath(initData.authApi.url, initData.authApi.basePath));
      set('paymentsApiUri', joinPath(initData.paymentsApi.url, initData.paymentsApi.basePath));

      // ------- INGESTOR adds --------
      set('ingestApiUri', joinPath(initData.ingestApi.url, initData.ingestApi.basePath));
      set('geopingApiUri', joinPath(initData.geopingApi.url, initData.geopingApi.basePath));
      set('skyApiUri', initData.skyApi.url)
      // ----- END INGESTOR adds ------

      set('resourceProxyUri', joinPath(initData.resourceProxyUri, ''));
      set('appInfo', initData.appInfo);
      set('segmentIOKey', initData.segmentIOKey);
      set('ingestorSegmentKey', initData.ingestorSegmentKey);
      set('ingestorHotjarCode', initData.ingestorHotjarCode);
      // properly format firebase object
      const dbName = 'apollo';
      const firebase = {};

      firebase[dbName] = initData.firebase;
      set('firebase', firebase);
      set('overlordUrl', joinPath(initData.overlordUrl, ''));
      const uploaderUrl = initData.appInfo.env === 'staging' ? 'https://upload-staging.skycatch.net' : 'https://upload.skycatch.com';
      set('uploadUrl', joinPath(uploaderUrl, ''));
      set('pubnub.payments', initData.pubnubPayments);
      set('pubnub.processingJobs', initData.pubnubProcessingJobs);

      set('googleAuthAppId', initData.googleAuthAppId);
      set('facebookAuthAppId', initData.facebookAuthAppId);

      set('sentryDSN', initData.sentryDSN);

      // set the user if it is not set... this should only be the case if
      // (1) the user has never visited our site on the device he/she is using
      // (2) the user logged out
      if (!get('user')) {
        const group = initData.user.group;

        set('user', initData.user);
        set('currentGroup', group);
        set('currentGroupAccess', initData.groups[group].access);
      }

      if (isAnonUser()) {
        set('groups', initData.groups);
        set('firebase', firebase);
      }
    }
  };

  _.extend(this, {
    init: init,
    clearAuth: clearAuth,
    get: get,
    getAll: getAll,
    getApiUri: getApiUri,
    getAuthApiUri: getAuthApiUri,
    getBetaFeatures: getBetaFeatures,
    getCurrentGroup: getCurrentGroup,
    getCurrentOrganization: getCurrentOrganization,
    getEnv: getEnv,
    getFirebaseToken: getFirebaseToken,
    getFirebaseUri: getFirebaseUri,
    // -------- INGESTOR ADDS -----------
    getIngestApiUri: getIngestApiUri,
    getGeopingApiUri: getGeopingApiUri,
    getSkyApiUri: getSkyApiUri,
    // -------- END INGESTOR ADDS -----------
    getPaymentsApiUri: getPaymentsApiUri,
    getResourceProxyUri: getResourceProxyUri,
    getToken: getToken,
    getUser: getUser,
    getUserLevel: getUserLevel,
    isAdminUser: isAdminUser,
    isAnonUser: isAnonUser,
    isBetaUser: isBetaUser,
    isIngestBetaUser: isIngestBetaUser,
    isOrganizationAdmin: isOrganizationAdmin,
    isOrganizationBillingAdmin: isOrganizationBillingAdmin,
    remove: remove,
    set: set,
    updateUserMeta: updateUserMeta,
    $$clearCache: $$clearCache
  });
};

module.exports = IdentityService;



// WEBPACK FOOTER //
// ./src/common/identity/IdentityService.js